import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import accountingApi from '../services/accountingApi';

function BankAccountForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        branch_name: '',
        account_holder_name: '',
        account_type: 'current',
        opening_balance: '0.00',
        is_active: true
    });

    const [loading, setLoading] = useState(isEdit);
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isEdit) {
            loadBankAccount();
        }
    }, [id]);

    const loadBankAccount = async () => {
        try {
            const response = await accountingApi.getBankAccount(id);
            if (response.success) {
                const account = response.data?.bank_account || response.data;
                if (account) {
                    setFormData({
                        bank_name: account.bank_name || '',
                        account_number: account.account_number || '',
                        ifsc_code: account.ifsc_code || '',
                        branch_name: account.branch_name || '',
                        account_holder_name: account.account_holder_name || '',
                        account_type: account.account_type || 'Current',
                        opening_balance: account.opening_balance || '0.00',
                        is_active: account.is_active !== undefined ? account.is_active : true
                    });
                }
            } else {
                setErrors([response.message || 'Failed to load bank account']);
            }
        } catch (error) {
            setErrors(['Failed to load bank account']);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setErrors([]);

            const response = isEdit
                ? await accountingApi.updateBankAccount(id, formData)
                : await accountingApi.createBankAccount(formData);

            if (response.success) {
                const accountData = response.data?.bank_account || response.data;
                if (accountData?.id) {
                    navigate(`/accounting/bank-accounts/${accountData.id}`);
                } else {
                    navigate('/accounting/bank-accounts');
                }
            } else {
                setErrors(response.errors || [response.message || 'Failed to save bank account']);
            }
        } catch (error) {
            setErrors(['Failed to save bank account']);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting/bank-accounts" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">
                        {isEdit ? 'Edit Bank Account' : 'Add Bank Account'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {errors.length > 0 && (
                    <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md">
                        <ul className="list-disc list-inside text-sm text-rose-700">
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-slate-700 mb-2">
                                Bank Name <span className="text-rose-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="bank_name"
                                value={formData.bank_name}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                placeholder="e.g., HDFC Bank, ICICI Bank"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">
                                Account Number <span className="text-rose-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="account_number"
                                value={formData.account_number}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                placeholder="Account number"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">
                                IFSC Code <span className="text-rose-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="ifsc_code"
                                value={formData.ifsc_code}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                placeholder="e.g., HDFC0001234"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Branch Name</label>
                            <input
                                type="text"
                                name="branch_name"
                                value={formData.branch_name}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                placeholder="Branch name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Account Holder Name</label>
                            <input
                                type="text"
                                name="account_holder_name"
                                value={formData.account_holder_name}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                placeholder="Account holder name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Account Type</label>
                            <select
                                name="account_type"
                                value={formData.account_type}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            >
                                <option value="current">Current</option>
                                <option value="savings">Savings</option>
                                <option value="od">Overdraft (OD)</option>
                                <option value="cc">Cash Credit (CC)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Opening Balance (₹)</label>
                            <input
                                type="number"
                                name="opening_balance"
                                value={formData.opening_balance}
                                onChange={handleChange}
                                step="0.01"
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                placeholder="0.00"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active}
                                onChange={handleChange}
                                className="w-5 h-5 text-blue-600 border-2 border-slate-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-semibold text-slate-700">Active Account</span>
                        </label>
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        type="submit"
                        disabled={saving}
                        className={`flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {saving ? 'Saving...' : (isEdit ? 'Update Account' : 'Add Account')}
                    </button>
                    <Link
                        to="/accounting/bank-accounts"
                        className="px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors text-center"
                    >
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    );
}

export default BankAccountForm;
